import React from 'react';
import { Link } from 'react-router-dom';
import { getAdjacentUpdates, getUpdateCategoryLabel } from '../../../data/help';
import HelpArticle from './HelpArticle';
import { getHelpArticlePath } from './helpFormat';

const isInternalPath = (path = '') => path.startsWith('/') && !path.startsWith('//');
const isSafeLink = (path = '') => isInternalPath(path) || /^https:\/\//.test(path);
const isStaticFile = (path = '') => /\.(pdf|zip)$/i.test(path);

const UpdateLinks = ({ links }) => (
  <div className="help-update-links">
    {links.map((link, index) => {
      const className = `help-update-links__button${index === 0 ? ' help-update-links__button--primary' : ''}`;
      if (isInternalPath(link.path) && isStaticFile(link.path)) {
        return <a key={link.path} href={link.path} download className={className}>{link.label}</a>;
      }
      if (isInternalPath(link.path)) {
        return <Link key={link.path} to={link.path} className={className}>{link.label}</Link>;
      }
      return (
        <a key={link.path} href={link.path} target="_blank" rel="noopener noreferrer" className={className}>
          {link.label}
        </a>
      );
    })}
  </div>
);

const UpdateSections = ({ sections }) => (
  <div className="help-article__content help-update-sections">
    {sections.map(section => (
      <section key={section.title} className="help-update-sections__section">
        <h2>{section.title}</h2>
        {section.description && <p>{section.description}</p>}
        {section.image?.src && (
          <figure className="help-update-figure">
            <img src={section.image.src} alt={section.image.alt || ''} loading="lazy" decoding="async" />
            {section.image.caption && <figcaption>{section.image.caption}</figcaption>}
          </figure>
        )}
        {section.items?.length > 0 && (
          <ul>
            {section.items.map(item => <li key={item}>{item}</li>)}
          </ul>
        )}
      </section>
    ))}
  </div>
);

const AdjacentLink = ({ item, label, linkState, align }) => (
  <Link
    to={getHelpArticlePath(item.id)}
    state={linkState}
    className={`help-update-nav__link help-update-nav__link--${align}`}
  >
    <span className="help-update-nav__label">{label}</span>
    <span className="help-update-nav__title">{item.title}</span>
  </Link>
);

const UpdateDetail = ({ item, backTo, linkState }) => {
  const { newer, older } = getAdjacentUpdates(item.id);
  const hasSections = Array.isArray(item.sections) && item.sections.length > 0;
  const links = (item.links || []).filter(link => link?.label && isSafeLink(link?.path));

  return (
    <HelpArticle
      tab="updates"
      backTo={backTo}
      date={item.date}
      title={item.title}
      badges={(
        <>
          {item.summary && <p className="help-update-summary">{item.summary}</p>}
          {item.categories?.length > 0 && (
            <div className="help-card__chips">
              {item.categories.map(category => (
                <span key={category} className="help-chip">{getUpdateCategoryLabel(category)}</span>
              ))}
            </div>
          )}
        </>
      )}
      after={(newer || older) && (
        <nav className="help-update-nav" aria-label="다른 업데이트">
          {older ? <AdjacentLink item={older} label="지난 업데이트" linkState={linkState} align="start" /> : <span />}
          {newer ? <AdjacentLink item={newer} label="최근 업데이트" linkState={linkState} align="end" /> : <span />}
        </nav>
      )}
    >
      {hasSections
        ? <UpdateSections sections={item.sections} />
        : <div className="help-article__content" dangerouslySetInnerHTML={{ __html: item.content || '' }} />}
      {links.length > 0 && <UpdateLinks links={links} />}
    </HelpArticle>
  );
};

export default UpdateDetail;
